/**
 * siteContentDefaults.ts
 * All default data values for site content sections.
 * Types imported from siteContentTypes.ts.
 */
import type {
  HeroData,
  WhatsIncludedData,
  SpaceOption,
  SpacesSectionData,
  AppSectionData,
  MissionData,
  MeetMakersData,
  FAQSectionData,
  FAQItem,
  ContactInfo,
  FooterData,
  NavLink,
  NotifyBannerData,
  GalleryPhoto,
  LogoConfig,
  ArtistEntry,
  InstagramPost,
} from "./siteContentTypes";

// ─── Hero Section ───────────────────────────────────────────────────────────

export const defaultHero: HeroData = {
  badge: "Coming Soon \u2014 Rogue Valley, Medford OR",
  headline1: "Your Art Deserves a",
  headlineAccent: "Home",
  tagline: "100% of each sale goes to the artist.",
  body: "A permanent, algorithm-free home for independent makers \u2014 where your work is seen, celebrated, and sold year-round.",
  ctaPrimary: "Start Your Application",
  ctaSecondary: "View Spaces",
  ctaPrimaryUrl: "#application",
  ctaSecondaryUrl: "#spaces",
  stats: [
    { value: "100%", label: "Sales to Artist" },
    { value: "5", label: "Space Types" },
    { value: "Jury", label: "Selection Process" },
  ],
  bgImage: "",
};

// ─── What's Included Section ──────────────────────────────────────────────

export const defaultWhatsIncluded: WhatsIncludedData = {
  eyebrow: "What Your Rent Covers",
  heading: "We Handle Everything. You Just Create.",
  subheading: "Your monthly rental isn't just a shelf \u2014 it's a full-service partnership. Here's what we take care of so you don't have to.",
  items: [
    { icon: "ri-bank-card-line", title: "Point of Sale System", body: "We handle all transactions in-store with a modern POS system. No setup, no fees, no headaches on your end." },
    { icon: "ri-user-smile-line", title: "Dedicated Sales Staff", body: "Our knowledgeable staff are on the floor every day, talking to customers about your work and helping make sales happen." },
    { icon: "ri-home-4-line", title: "Rent & Utilities", body: "Your monthly rate covers your space in full \u2014 including electricity, heating, cooling, and all building costs." },
    { icon: "ri-exchange-dollar-line", title: "Transaction Fees", body: "We absorb all credit card and payment processing fees. The price you set is the amount you receive." },
    { icon: "ri-shield-check-line", title: "Security & Insurance", body: "The shop is secured and insured. Your work is protected while it's in our care." },
    { icon: "ri-megaphone-line", title: "Marketing & Promotion", body: "We promote the shop and its artists through social media, local events, and community outreach \u2014 at no extra cost to you." },
  ],
  closingNote: "All you need to do is stock your space and create. We take care of the rest.",
  extraBlocks: [],
};

// ─── Spaces Section ─────────────────────────────────────────────────────────

export const defaultSpaces: SpaceOption[] = [
  {
    id: "showcase",
    title: "Showcase Shelf",
    subtitle: "For makers wishing to trial the space.",
    price: "$75/mo",
    term: "1 month maximum. Reapply quarterly.",
    details: [
      "Shelf provided.",
      "Display: Provided by artist.",
      "Dimensions: 13.9 in x 12 in x 14.5 in",
      "4 tiers",
    ],
    highlight: false,
    image: "",
  },
  {
    id: "standard",
    title: "Standard Shelf",
    subtitle: "Get in front of a new audience!",
    price: "$120/mo",
    term: "3 month minimum.",
    details: [
      "Shelf provided.",
      "Display provided by artist.",
      "Dimensions: 13.9 in x 12 in x 14.5 in",
      "4 tiers",
      "Backstock: 14.8 in x 10.1 in x 6.25 in",
    ],
    highlight: false,
    image: "",
  },
  {
    id: "premium",
    title: "Premium Shelf",
    subtitle: "Perfect for larger makes or inventory!",
    price: "$180/mo",
    term: "3 month minimum.",
    details: [
      "Shelf provided.",
      "Display provided by artist.",
      "Dimensions: 27.95 in x 12 in x 14.5 in",
      "4 tiers",
      "Backstock: 14 in x 10 in x 10.75 in",
    ],
    highlight: false,
    image: "",
  },
  {
    id: "standard-wall",
    title: "Standard Wall Display",
    subtitle: "Utilize the wall for signage and art!",
    price: "$150/mo",
    term: "3 month minimum.",
    details: [
      "Artist provides shelf and display.",
      "Max Dimensions: 2 ft x 1.5 ft x 7 ft",
      "Backstock: 14 in x 10 in x 10.75 in",
    ],
    highlight: false,
    image: "",
  },
  {
    id: "premium-wall",
    title: "Premium Wall Display",
    subtitle: "Maximum space for your biggest statement!",
    price: "$220/mo",
    term: "3 month minimum.",
    details: [
      "Artist provides shelf and display.",
      "Max Dimensions: 3 ft x 1.5 ft x 7 ft",
      "Backstock: 14 in x 10 in x 10.75 in",
    ],
    highlight: false,
    image: "",
  },
];

export const defaultSpacesSection: SpacesSectionData = {
  eyebrow: "Available Spaces",
  heading: "Choose Your Perfect Display Space",
  subheading: "From trial shelves to full wall displays \u2014 find the space that fits your art and your goals.",
  spaces: defaultSpaces,
  bottomHelperText: "Not sure which space is right for you? Contact us and we'll help you decide.",
  extraBlocks: [],
};

// ─── Application Section ──────────────────────────────────────────────────

export const defaultApplication: AppSectionData = {
  heading: "Tell Us About Your Art",
  subheading: "Apply Today",
  body: "We review applications weekly and respond within 5 business days.",
  quote: "I spent years at artist alley events and maker fairs \u2014 watching incredible work get packed up Sunday night and forgotten by Monday. Artists deserve a permanent stage. That\u2019s why I built this.",
  quoteAuthor: "\u2014 The Founder, Makers Alley Co.",
  infoCards: [
    { icon: "ri-time-line", color: "text-pink-500", title: "Quick Review", desc: "Initial response within 5 business days" },
    { icon: "ri-shield-check-line", color: "text-green-500", title: "No Setup Fee", desc: "Just your monthly rental rate" },
    { icon: "ri-money-dollar-circle-line", color: "text-pink-500", title: "100% to You", desc: "Keep every dollar of your sales" },
  ],
  photos: [
    { src: "", alt: "Artist stocking shelf" },
    { src: "", alt: "Jewelry display" },
    { src: "", alt: "Art prints display" },
    { src: "", alt: "Happy artist" },
  ],
  successTitle: "Application Submitted!",
  successBody: "Thank you for applying!  We will contact you within 5 business days.  The jury selection process may take up to 30 days, at which time you will receive an update on your application status.",
  submitLabel: "Submit Application",
  fields: {
    fullName: { label: "Full Name *", placeholder: "Jane Smith" },
    email: { label: "Email Address *", placeholder: "jane@example.com" },
    phone: { label: "Phone Number", placeholder: "(555) 000-0000" },
    instagram: { label: "Social Media Handle", placeholder: "@yourhandle" },
    yearsCreating: { label: "Years Creating *", placeholder: "e.g. 3" },
    description: { label: "Describe Your Work *", placeholder: "Tell us about your handmade items, your creative process, and what makes your work unique..." },
    portfolio: { label: "Portfolio / Website URL", placeholder: "https://yourwebsite.com or Etsy link" },
  },
  artCategories: [
    "Ceramics & Pottery", "Jewelry & Accessories", "Paintings & Prints",
    "Textiles & Fiber Art", "Candles & Soaps", "Woodworking",
    "Sculpture", "Photography", "Mixed Media", "Other",
  ],
  spaceTypes: [
    "Showcase Shelf ($75/mo)", "Standard Shelf ($120/mo)",
    "Premium Shelf ($180/mo)", "Standard Wall Display ($150/mo)", "Premium Wall Display ($220/mo)",
  ],
  rulesAcknowledgmentText: "I have read and agree to the Rules & Guidelines of Makers Alley Co., including product guidelines, billing policies, and community standards. This field is required.",
  instagramHelperText: "Or email us your photo!",
  phoneHelperText: "Optional",
  extraBlocks: [],
};

// ─── Mission Section ────────────────────────────────────────────────────────

export const defaultMission: MissionData = {
  eyebrow: "Our Mission",
  heading: "Art Deserves a Permanent Stage",
  founderStory: "I've spent years as an artist \u2014 setting up at artist alley events, hauling tables to maker fairs, and pouring my heart into work that only got seen for a weekend. I watched incredible artists fight the Etsy algorithm, lose sales to social media shadowbans, and give up a cut of every sale just to be visible. I knew there had to be a better way.\n\nMakers Alley Co. was born from that frustration \u2014 and that love. A place where your work is displayed year-round, in front of real people walking through the door, with zero algorithm standing between you and your audience. You set your price. You keep your revenue. We take care of everything else.",
  founderName: "The Founder",
  founderTitle: "Artist, Maker & Founder of Makers Alley Co.",
  founderImage: "",
  missionStatement: "To give independent artists a permanent, algorithm-free home where their work is seen, celebrated, and sold \u2014 and where every dollar of every sale stays with the maker who earned it.",
  pillars: [
    { icon: "ri-store-2-line", title: "Year-Round Visibility", body: "No more packing up Sunday night. Your work stays on display every day the shop is open." },
    { icon: "ri-money-dollar-circle-line", title: "You Keep 100%", body: "Set your own prices. Every sale goes directly to you \u2014 no commission, no hidden cuts." },
    { icon: "ri-shield-check-line", title: "Algorithm-Free", body: "No fighting Etsy rankings or Instagram reach. Real foot traffic, real customers, real sales." },
    { icon: "ri-community-line", title: "A Real Community", body: "Be part of a curated collective of makers who support and inspire each other." },
  ],
  extraBlocks: [],
};

// ─── Meet Our Makers Section ────────────────────────────────────────────────

export const defaultMeetMakers: MeetMakersData = {
  eyebrow: "The Makers",
  heading: "Meet Our Artists",
  subheading: "Every item in our shop is handcrafted by an independent local artist. These are the talented makers behind the work.",
  seeMoreLabel: "See All Our Artists",
  extraBlocks: [],
};

export const defaultArtists: ArtistEntry[] = [
  {
    id: "artist-1",
    name: "Luna Ceramics",
    medium: "Ceramics & Pottery",
    bio: "Hand-thrown stoneware inspired by the Pacific Northwest landscape. Each piece is one-of-a-kind.",
    website: "https://example.com",
    image: "",
    hasWebsite: true,
    hasImage: true,
    objectPosition: "50% 50%",
  },
  {
    id: "artist-2",
    name: "Wildflower Press",
    medium: "Art Prints & Illustration",
    bio: "Botanical illustrations and nature-inspired prints, printed on archival paper in small batches.",
    website: "https://example.com",
    image: "",
    hasWebsite: true,
    hasImage: true,
    objectPosition: "50% 50%",
  },
  {
    id: "artist-3",
    name: "Ember & Thread",
    medium: "Fiber Art & Textiles",
    bio: "Macram\u00e9 wall hangings, woven tapestries, and fiber jewelry made with natural materials.",
    website: "https://example.com",
    image: "",
    hasWebsite: true,
    hasImage: true,
    objectPosition: "50% 50%",
  },
  {
    id: "artist-4",
    name: "Sage & Stone Jewelry",
    medium: "Handmade Jewelry",
    bio: "Sterling silver and gemstone jewelry with a minimalist, nature-inspired aesthetic.",
    website: "https://example.com",
    image: "",
    hasWebsite: true,
    hasImage: true,
    objectPosition: "50% 50%",
  },
  {
    id: "artist-5",
    name: "Rogue Valley Candle Co.",
    medium: "Soy Candles & Home Goods",
    bio: "Small-batch soy candles with locally-sourced botanicals and hand-poured in Medford, Oregon.",
    website: "https://example.com",
    image: "",
    hasWebsite: true,
    hasImage: true,
    objectPosition: "50% 50%",
  },
  {
    id: "artist-6",
    name: "Mossy Oak Studio",
    medium: "Watercolor & Mixed Media",
    bio: "Dreamy watercolor landscapes and abstract pieces celebrating the beauty of Southern Oregon.",
    website: "https://example.com",
    image: "",
    hasWebsite: true,
    hasImage: true,
    objectPosition: "50% 50%",
  },
];

// ─── FAQ Section ────────────────────────────────────────────────────────────

function ensureIds(items: (FAQItem | { q: string; a: string })[]): FAQItem[] {
  return items.map((item, i) => ({
    id: (item as FAQItem).id || `faq-${i}`,
    q: item.q,
    a: item.a,
  }));
}

export const defaultFAQ: FAQSectionData = {
  eyebrow: "Frequently Asked",
  heading: "Everything You Need to Know",
  subtext: "Can't find your answer?",
  contactLinkLabel: "Contact us directly",
  stats: [
    { value: "5 days", label: "Initial Response" },
    { value: "30 days", label: "Jury Decision" },
    { value: "Jury", label: "Selection Process" },
    { value: "5", label: "Space Types" },
  ],
  items: ensureIds([
    { q: "Who can apply for a space?", a: "Any independent maker or artist who creates handmade goods is welcome to apply. All items must be made by you \u2014 no reselling of mass-produced products." },
    { q: "How long does the application process take?", a: "We aim to respond to all inquiries within 5 business days. Artists are selected by jury \u2014 you will receive a decision or waitlist notification within 30 days of applying." },
    { q: "Can I restock my shelf whenever I want?", a: "Yes! You can restock during our open hours (Tuesday\u2013Sunday, 10am\u20136pm). We ask that you keep your space looking full and tidy at all times." },
    { q: "What happens if my items don't sell?", a: "At the end of your term, you're welcome to collect any unsold items. We encourage you to refresh your inventory regularly to keep things exciting for shoppers." },
    { q: "Can I display items from other artists?", a: "No \u2014 your space must only contain items you personally made. Collaboration pieces are allowed if you are one of the creators." },
    { q: "Is there a setup fee?", a: "There is no setup fee. You simply pay your monthly rental rate and we handle the rest \u2014 including point-of-sale, customer service, and store operations." },
    { q: "How many space types are available?", a: "We offer 5 space types: Showcase Shelf, Standard Shelf, Premium Shelf, Standard Wall Display, and Premium Wall Display. Each varies in size and monthly rate to fit different needs." },
    { q: "What if I don't have my work posted on social media?", a: "No problem at all! If you do not have any product photos available online, please email images of your work to info@makersalley.co for jury selection. We welcome emerging artists and understand not everyone has an established online presence." },
    { q: "What is your return policy?", a: "Changed your mind? You have 14 days to exchange or return a product to the same location it was purchased from. The product must be in its original condition, both unused and unworn to qualify for a return or exchange. If you have any additional questions please reach out to the store in which you made your purchase. Thank you!" },
  ]),
  extraBlocks: [],
};

// ─── Contact Section ────────────────────────────────────────────────────────

export const defaultContact: ContactInfo = {
  heading: "Visit Us or Say Hello",
  subheading: "Get In Touch",
  intro: "We're coming soon to the Rogue Valley! In the meantime, reach out and we'll get back to you.",
  followAlongLabel: "Follow Along",
  socialLinks: [
    { id: "instagram", icon: "ri-instagram-line", label: "Instagram", href: "#" },
    { id: "tiktok", icon: "ri-tiktok-line", label: "TikTok", href: "#" },
    { id: "email", icon: "ri-mail-line", label: "Email", href: "mailto:info@makersalley.co" },
  ],
  formTitle: "Send Us a Message",
  successTitle: "Message Sent!",
  successBody: "We'll get back to you as soon as we can.",
  submitLabel: "Send Message",
  fields: {
    name: { label: "Your Name *", placeholder: "Jane Smith" },
    email: { label: "Email Address *", placeholder: "jane@example.com" },
    subject: { label: "Subject *", placeholder: "What's this about?" },
    message: { label: "Message *", placeholder: "Tell us how we can help..." },
  },
  infoCards: [
    {
      id: "address",
      icon: "ri-map-pin-line",
      iconBg: "bg-pink-100",
      iconColor: "text-pink-500",
      title: "Visit Us",
      lines: ["Coming Soon", "Rogue Valley \u2014 Medford, Oregon"],
      visible: true,
    },
    {
      id: "hours",
      icon: "ri-time-line",
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
      title: "Store Hours",
      lines: ["Hours TBD", "Opening Soon!"],
      visible: true,
    },
    {
      id: "email",
      icon: "ri-mail-line",
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
      title: "Email Us",
      lines: ["info@makersalley.co"],
      visible: true,
    },
  ],
  phoneHelperText: "Optional",
  emailHelperText: "Or email us your photo!",
  extraBlocks: [],
};

// ─── Footer ─────────────────────────────────────────────────────────────────

export const defaultFooter: FooterData = {
  tagline: "Coming soon to the Rogue Valley in Medford, Oregon. A permanent, algorithm-free home for independent makers.",
  bottomTagline: "Handmade with love by local artists.",
  visitTitle: "Visit Us",
  visitAddress: "Coming Soon \u2014 Rogue Valley, Medford, Oregon",
  visitHours: "Hours TBD \u2014 Opening Soon!",
  visitEmail: "info@makersalley.co",
  socialLinks: [
    { id: "instagram", icon: "ri-instagram-line", label: "Instagram", href: "#", visible: true },
    { id: "tiktok", icon: "ri-tiktok-line", label: "TikTok", href: "#", visible: true },
    { id: "email", icon: "ri-mail-line", label: "Email", href: "mailto:info@makersalley.co", visible: true },
  ],
  forArtistsTitle: "For Artists",
  spacesTitle: "Spaces",
  visitSectionTitle: "Visit Us",
  forArtistsLinks: [
    { id: "fa-mission", label: "Our Mission", href: "#mission", external: false },
    { id: "fa-included", label: "What's Included", href: "#whatsIncluded", external: false },
    { id: "fa-application", label: "Application Process", href: "#application", external: false },
    { id: "fa-spaces", label: "Space Options", href: "#spaces", external: false },
    { id: "fa-rules", label: "Rules & Guidelines", href: "#rules", external: false },
    { id: "fa-faq", label: "FAQ", href: "#faq", external: false },
    { id: "fa-artists", label: "Our Artists", href: "/artists", external: true },
    { id: "fa-careers", label: "Careers", href: "/careers", external: true },
    { id: "fa-testimonials", label: "Reviews", href: "/testimonials", external: true },
    { id: "fa-review", label: "Leave a Review", href: "/leave-a-review", external: true },
  ],
  spacesLinks: [
    { id: "sp-showcase", label: "Showcase Shelf", href: "#spaces", external: false },
    { id: "sp-standard", label: "Standard Shelf", href: "#spaces", external: false },
    { id: "sp-premium", label: "Premium Shelf", href: "#spaces", external: false },
    { id: "sp-standard-wall", label: "Standard Wall Display", href: "#spaces", external: false },
    { id: "sp-premium-wall", label: "Premium Wall Display", href: "#spaces", external: false },
  ],
};

// ─── Navbar ─────────────────────────────────────────────────────────────────

export const defaultNavLinks: NavLink[] = [
  { id: "home", label: "Home", href: "#home", external: false, inTopBar: true },
  { id: "spaces", label: "Spaces", href: "#spaces", external: false, inTopBar: true },
  { id: "rules", label: "Rules", href: "#rules", external: false, inTopBar: true },
  { id: "contact", label: "Contact Us", href: "#contact", external: false, inTopBar: true },
  { id: "whats-included", label: "What's Included", href: "#whatsIncluded", external: false, inTopBar: false },
  { id: "apply", label: "Apply", href: "#application", external: false, inTopBar: false },
  { id: "faq", label: "FAQ", href: "#faq", external: false, inTopBar: false },
  { id: "our-artists", label: "Our Artists", href: "/artists", external: true, inTopBar: false },
  { id: "careers", label: "Careers", href: "/careers", external: true, inTopBar: false },
  { id: "testimonials", label: "Reviews", href: "/testimonials", external: true, inTopBar: false },
  { id: "leave-review", label: "Leave a Review", href: "/leave-a-review", external: true, inTopBar: false },
];

// ─── Notify Banner ─────────────────────────────────────────────────────────

export const defaultNotifyBanner: NotifyBannerData = {
  visible: true,
  text: "Be the first to know when we open our doors!",
  placeholder: "Enter your email address",
  buttonLabel: "Notify Me",
  successText: "You're on the list! We'll be in touch.",
  bgColor: "#1a1a2e",
};

// ─── Gallery Built-in Photos ────────────────────────────────────────────────

export const builtinPhotos: GalleryPhoto[] = [
  {
    id: "EL2HOZldhD0",
    url: "https://unsplash.com/photos/EL2HOZldhD0/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/EL2HOZldhD0/download?w=800&force=true",
    label: "Soap Making \u2014 Cutting Cubes",
    isHero: true,
  },
  {
    id: "Zdnqtpj79zM",
    url: "https://unsplash.com/photos/Zdnqtpj79zM/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/Zdnqtpj79zM/download?w=800&force=true",
    label: "Fashion Designer & Client",
    isHero: true,
  },
  {
    id: "a_grCMmrWpY",
    url: "https://unsplash.com/photos/a_grCMmrWpY/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/a_grCMmrWpY/download?w=800&force=true",
    label: "Pottery Shop \u2014 Customer",
    isHero: true,
  },
  {
    id: "so7W_zVxdbo",
    url: "https://unsplash.com/photos/so7W_zVxdbo/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/so7W_zVxdbo/download?w=800&force=true",
    label: "White Dishes & Cups Shelf",
    isHero: true,
  },
  {
    id: "emh-EKyCZWY",
    url: "https://unsplash.com/photos/emh-EKyCZWY/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/emh-EKyCZWY/download?w=800&force=true",
    label: "Rustic Table \u2014 Food & Dishes",
    isHero: true,
  },
  {
    id: "uMc8et3Hiko",
    url: "https://unsplash.com/photos/uMc8et3Hiko/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/uMc8et3Hiko/download?w=800&force=true",
    label: "Cups & Saucers on Table",
    isHero: true,
  },
  {
    id: "Gem6g4z9M3E",
    url: "https://unsplash.com/photos/Gem6g4z9M3E/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/Gem6g4z9M3E/download?w=800&force=true",
    label: "Handmade Dishes Display",
    isHero: true,
  },
  {
    id: "kqz5EcqiE1o",
    url: "https://unsplash.com/photos/kqz5EcqiE1o/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/kqz5EcqiE1o/download?w=800&force=true",
    label: "Three Shelves \u2014 Vases & Items",
    isHero: true,
  },
  {
    id: "aujK6njkcVY",
    url: "https://unsplash.com/photos/aujK6njkcVY/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/aujK6njkcVY/download?w=800&force=true",
    label: "Florist & Customer",
    isHero: true,
  },
  {
    id: "UFZljdOh71A",
    url: "https://unsplash.com/photos/UFZljdOh71A/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/UFZljdOh71A/download?w=800&force=true",
    label: "Women Working in Workshop",
    isHero: true,
  },
  {
    id: "nmunb0BfYkY",
    url: "https://unsplash.com/photos/nmunb0BfYkY/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/nmunb0BfYkY/download?w=800&force=true",
    label: "Woman Holding Hat with Tag",
    isHero: false,
  },
  {
    id: "FE7ayGJeVps",
    url: "https://unsplash.com/photos/FE7ayGJeVps/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/FE7ayGJeVps/download?w=800&force=true",
    label: "Group Around Table (1)",
    isHero: true,
  },
  {
    id: "gRUUphAWvXo",
    url: "https://unsplash.com/photos/gRUUphAWvXo/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/gRUUphAWvXo/download?w=800&force=true",
    label: "Group Around Table (2)",
    isHero: true,
  },
  {
    id: "AfJHw6xjNxg",
    url: "https://unsplash.com/photos/AfJHw6xjNxg/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/AfJHw6xjNxg/download?w=800&force=true",
    label: "Jewelry Purchase at Counter",
    isHero: true,
  },
  {
    id: "EayG14Sj4-U",
    url: "https://unsplash.com/photos/EayG14Sj4-U/download?w=1920&force=true",
    thumb: "https://unsplash.com/photos/EayG14Sj4-U/download?w=800&force=true",
    label: "Woman with White Shopping Bag",
    isHero: false,
  },
  {
    id: "branded-bag-1",
    url: "",
    thumb: "",
    label: "Makers Alley Co Bag (Market)",
    isHero: false,
  },
  {
    id: "branded-bag-2",
    url: "",
    thumb: "",
    label: "Makers Alley Co Bag (White)",
    isHero: false,
  },
  {
    id: "original-hero",
    url: "",
    thumb: "",
    label: "Original Hero \u2014 Boutique Interior",
    isHero: true,
  },
  {
    id: "kawaii-shop-hero",
    url: "",
    thumb: "",
    label: "Kawaii Art Shop Interior",
    isHero: true,
  },
];

export const defaultSectionLabels: Record<string, string> = {
  hero: "Hero",
  whatsIncluded: "What's Included",
  whyWeExist: "Why We Exist",
  spaces: "Spaces",
  application: "Artist Application",
  rules: "Rules",
  mission: "Our Mission",
  meetMakers: "Meet Our Makers",
  faq: "FAQ",
  contact: "Contact",
  instagramFeed: "Instagram Feed",
};

// ─── Admin Context Defaults ─────────────────────────────────────────────────

export const defaultSectionVisibility: Record<string, boolean> = {
  hero: true,
  whatsIncluded: true,
  whyWeExist: true,
  spaces: true,
  application: true,
  rules: true,
  mission: true,
  meetMakers: true,
  faq: true,
  contact: true,
  instagramFeed: true,
};

export const defaultSectionOrder = [
  "hero",
  "whatsIncluded",
  "whyWeExist",
  "spaces",
  "application",
  "rules",
  "mission",
  "meetMakers",
  "faq",
  "contact",
];

export const defaultLogoConfig: LogoConfig = {
  mode: "icon-text",
  icon: "ri-store-2-line",
  text: "makers alley",
  imageUrl: "",
  suffixText: "co",
  suffixColor: "#22c55e",
  suffixItalic: true,
  suffixBold: false,
};

// ─── Instagram Posts ────────────────────────────────────────────────────────

export const defaultInstagramPosts: InstagramPost[] = [
  {
    id: "post-1",
    url: "https://www.instagram.com/p/DFakeShortcode1/",
    caption: "Shop opening sneak peek",
  },
  {
    id: "post-2",
    url: "https://www.instagram.com/p/DFakeShortcode2/",
    caption: "Meet our first makers",
  },
  {
    id: "post-3",
    url: "https://www.instagram.com/p/DFakeShortcode3/",
    caption: "Behind the scenes",
  },
];

// ─── Why We Exist Section ───────────────────────────────────────────────────

export const defaultWhyWeExist = {
  eyebrow: "Our Reason for Being",
  heading: "Artists Deserve More Than a Weekend Booth",
  body: "Every artist has lived the same cycle: haul your work to a market, set up at dawn, pray for foot traffic, then pack everything up when the gates close. We built Makers Alley because that cycle is broken.\n\nYou deserve a space where your work lives and sells year-round. A place where customers walk in specifically to discover handmade art. A community that celebrates what you create — without taking a cut of every sale.",
  quote: "I spent years watching incredible artists fight algorithms, pay platform fees, and pack up their dreams every Sunday night. Makers Alley is the answer I wish I had.",
  quoteAuthor: "— The Founder, Makers Alley Co.",
  values: [
    { icon: "ri-store-2-line", title: "Permanent Space", body: "Your work stays on display every day the shop is open. No more setting up and tearing down." },
    { icon: "ri-money-dollar-circle-line", title: "100% to You", body: "You keep every dollar you earn. We absorb transaction fees, platform costs, and operational overhead." },
    { icon: "ri-shield-check-line", title: "Algorithm-Free", body: "No social media games. No SEO battles. Just real people walking in and falling in love with your work." },
  ],
  ctaLabel: "See How It Works",
  ctaHref: "#whatsIncluded",
  extraBlocks: [],
};